<template>
  <div class="ais-Hits-wrapper">
    <div>
      <ol class="ais-Hits-list">
        <li
          class="ais-Hits-item"
          v-for="item in sortedItems"
          :key="item.objectID">
          <ResourcePreviewCard
            :key="item.uuid"
            :uuid="item.uuid"
            :title="item.title"
            :description="item.description"
            :type="item.type"
            :thumbnail_url="item.thumbnail_url"
            :grades="item.grades"
            :screenshots="item.screenshots || []"
            :subjects="item.subjects || item.subject || []"
            :download_links="item.download_links"
            :google_drive_links="item.google_drive_links"
            :aligned-content="filterAlignedContent(item.content_alignments)"
            :show-resource-type="false"
            :type-header="fromWizard ? '' : `${item.type} Assessment`"
            :set_number="item.set_number"
            :rationale="item.rationale"
            :relevance-score="item.relevance_score"
            :heading-level="3"
            @resourceDownloadClicked="sendEvent('click', item, 'Clicked Browse Assessments Result Download Dropdown')"
            @resourceDownloadOpened="handleCardClick(item, 'Opened Browse Assessments Result Download Link', sendEvent)"
            @resourcePreviewShown="handleCardClick(item, 'Clicked Browse Assessments Result Preview', sendEvent)"
            @resourceSaved="handleCardClick(item, 'Clicked Browse Assessments Result Save', sendEvent)"
          >
            <template slot="resource-details">
              <p class="text-muted resource-details" :class="{ 'wizard-resource-details': fromWizard }">
                <span v-if="showGrades(item) && item.grades">
                  {{ getGradesString(item.grades) }}
                </span>
                <span v-else-if="showLevels(item) && item.levels">
                  {{ getLevelsString(item.levels) }}
                </span>
                <span>
                  {{ getSubjectLabelsFromValues(item.subjects || item.subject).join(', ') }}
                </span>
                <span v-if="!isBehaviorSelected && item.domains"> ({{ item.domains.join(', ') }})</span>
              </p>
            </template>
          </ResourcePreviewCard>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
import ResourcePreviewCard from '@/components/ResourcePreviewCard';
import { isCoreSubject } from '@/util/core_subject';
import { mapGetters } from 'vuex';
import { intForGrade, gradeSort } from '@/util/gradesort'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'

export default {
  name: 'BrowseAssessmentsFilters',
  components: {
    ResourcePreviewCard
  },
  mixins: [
    noSelMixin
  ],
  props: {
    items: {
      type: Array,
      required: true
    },
    sendEvent: {
      type: Function,
      required: true
    },
    selectedSubject: {
      type: String,
      required: false,
      default: 'Reading'
    },
    categoryOptions: {
      type: Object,
      required: false,
      default: () => {}
    },
    showCategoryHeaders: {
      type: Boolean,
      required: true
    },
    fromWizard: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mounted() {
    this.sendReturnedResultsAnalyticsEvent()
  },
  eventContext() {
    return {
      from_browse_assessments: true
    }
  },
  computed: {
    ...mapGetters({
      isPremiumUser: 'user/isPremium',
      latestSearchId: 'agenticSearch/latestSearchId',
      latestSearchGrade: 'agenticSearch/latestSearchGrade',
    }),
    isBehaviorSelected() { return this.selectedSubject === 'Behavior'},
    sortedItems() {
      if (this.fromWizard) {
        return this.items
      }
      return [...this.items].sort((a, b) => {
        const getHighestGrade = (item) => {
          if (!item.grades || item.grades.length === 0) return -1
          const validGrades = item.grades.filter(g => g && g !== 'Mastery')
          if (validGrades.length === 0) return -1
          const sortedGrades = gradeSort([...validGrades])
          return intForGrade(sortedGrades[sortedGrades.length - 1])
        }
        return getHighestGrade(b) - getHighestGrade(a)
      })
    }
  },
  methods: {
    sendReturnedResultsAnalyticsEvent() {
      const eventData = {
        browse_assessment_num_results: this.items.length,
        algolia_query_id: this.items.length ? this.items[0].__queryID : '',
      }
      if (this.fromWizard) {
        eventData.agentic_search_id = this.latestSearchId
        eventData.selected_grade = this.latestSearchGrade
      }
      this.$sendEvent('Returned Browse Assessments Results', eventData)
    },
    isCoreSubject,
    isPMA(assessmentResult) {
      return assessmentResult.type === 'Progress Monitoring'
    },
    getSubjectLabelsFromValues(subjectValues) {
      if (!subjectValues || !subjectValues.length) {
        return []
      }
      const subjectValuesForProfile = this.assessmentSubjectOptionsForProfile.map(opt => opt.value)
      return subjectValues.filter(subjectValue => subjectValuesForProfile.includes(subjectValue))
    },
    handleCardClick(item, eventName, algoliaSendEventFunction) {
      algoliaSendEventFunction('click', item, eventName)
      algoliaSendEventFunction('conversion', item, eventName)
    },
    showGrades(assessmentResult) {
      const subjects = assessmentResult.subjects || assessmentResult.subject
      if (!subjects) {
        return false
      }
      return this.fromWizard || this.isPMA(assessmentResult) || (subjects.length > 0 && isCoreSubject(subjects[0]) && !this.isBehaviorSelected)
    },
    showLevels(assessmentResult) {
      if (!assessmentResult.subject) {
        return false
      }
      if (this.isBehaviorDataCollection(assessmentResult)) {
        return false;
      }

      if (this.isPMA(assessmentResult) && isCoreSubject(assessmentResult.subject[0])) {
        return false;
      }

      return true;
    },
    filterAlignedContent(alignedContent) {
      if (!alignedContent || !alignedContent.goals || !alignedContent.goals.length) {
        return null
      }
      return { goals: alignedContent.goals }
    },
    getGradesString(gradesArray) {
      const formattedGradesString = gradesArray
        .filter(g => !!g && g !== 'Mastery')
        .sort((a,b) => intForGrade(a) - intForGrade(b))
        .join(', ')
      return `Grade: ${formattedGradesString} · `
    },
    getLevelsString(levelsArray) {
      const formattedLevelsString = levelsArray.join(', ')
      return `Level: ${formattedLevelsString} · `
    },
    isBehaviorDataCollection(assessmentResult) {
      return assessmentResult.domains.includes('Behavior Data Collection')
    }
  },
}
</script>
<style type="text/less" scoped>
  .ais-Hits-item::v-deep {
    padding-top: var(--spacing-xs);
    padding-bottom: var(--spacing-xs);
    box-shadow: none;
  }
  h2:first-of-type::v-deep {
    margin-top: var(--spacing-xs);
  }
  .resource-details {
    margin-bottom: 1rem;
  }
  .wizard-resource-details {
    line-height: 16px;
  }
</style>
